import { useEffect, useState } from "react";
import {
  Box,
  Stack,
  CircularProgress,
  Typography,
  IconButton,
  Grid,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import { useFetchContext } from "../../../contexts/FetchContext";
import { PageContainer } from "../../shared/components/PageContainer";
import RoyaltyTable from "./components/RoyaltyTable";
import NoResult from "../../shared/components/NoResult";
import moment from "moment";

interface Product {
  id: string;
  uuid: string;
  productCode: string;
  serialNumber: string;
  quantity: number;
  royalty: number;
  warehouseLocationId: number;
  warehouseLocation: {
    name: string;
  };
}

interface Royalty {
  id: string;
  uuid: string;
  ddtNumber: string;
  externalId: string;
  createdAt: string;
  date: string;
  orderDateTime: string;
  shippingDateTime: string;
  carriageDescription: string;
  customerDisplayName: string;
  destinationDisplayName: string;
  shipmentSupplierCost: number;
  shipmentCost: number;
  province: string;
  products: Product[];
  warehouseId: number;
  status: string;
  location?: string;
}

export const RoyaltyComponent = () => {
  const { state, fetchData } = useFetchContext();

  const [selectedMonth, setSelectedMonth] = useState<string>("");
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState<number>(currentYear);
  const [royalties, setRoyalties] = useState<Royalty[]>([]);
  const [customerOrderDates, setCustomerOrderDates] = useState<
    Map<string, string[]>
  >(new Map());
  // Rimossa la paginazione

  const currentMonth = new Date().getMonth() + 1;
  const allMonths = [
    { value: "1", label: "Gennaio" },
    { value: "2", label: "Febbraio" },
    { value: "3", label: "Marzo" },
    { value: "4", label: "Aprile" },
    { value: "5", label: "Maggio" },
    { value: "6", label: "Giugno" },
    { value: "7", label: "Luglio" },
    { value: "8", label: "Agosto" },
    { value: "9", label: "Settembre" },
    { value: "10", label: "Ottobre" },
    { value: "11", label: "Novembre" },
    { value: "12", label: "Dicembre" },
  ];
  const months =
    selectedYear === currentYear
      ? allMonths.filter((month) => parseInt(month.value) <= currentMonth)
      : allMonths;

  useEffect(() => {
    if (selectedMonth) {
      fetchRoyalties();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMonth, selectedYear]);

  // Identificazione degli ordini multipli dello stesso cliente in un giorno
  useEffect(() => {
    if (royalties.length > 0) {
      const orderDatesByCustomerAndDestination = new Map<string, string[]>();

      royalties.forEach((royalty) => {
        const customer = royalty.customerDisplayName;
        const destination = royalty.destinationDisplayName;
        // Consideriamo solo la data, ignorando l'orario
        const orderDate = moment(royalty.createdAt).format("YYYY-MM-DD");
        const key = `${customer}-${destination}-${orderDate}`;

        if (!orderDatesByCustomerAndDestination.has(key)) {
          orderDatesByCustomerAndDestination.set(key, []);
        }

        const orders = orderDatesByCustomerAndDestination.get(key);
        if (orders && !orders.includes(royalty.id)) {
          orders.push(royalty.id);
        }
      });

      setCustomerOrderDates(orderDatesByCustomerAndDestination);
    }
  }, [royalties]);

  const fetchRoyalties = async () => {
    try {
      const response = await fetchData(
        `/documents/royalties?month=${selectedMonth}&year=${selectedYear}`
      );

      console.log(response.data);
      if (response && response.data) {
        // Ordina per data (solo giorno, ignorando l'orario) e cliente
        const sortedRoyalties = [...response.data].sort((a, b) => {
          // Prima ordina per data (solo giorno)
          const dateA = moment(a.createdAt).startOf("day");
          const dateB = moment(b.createdAt).startOf("day");
          const dateCompare = dateA.valueOf() - dateB.valueOf();

          if (dateCompare !== 0) return dateCompare;

          // Se la data è uguale, ordina per cliente
          return a.customerDisplayName.localeCompare(b.customerDisplayName);
        });

        setRoyalties(sortedRoyalties);
      }
    } catch (error) {
      console.error("Error fetching royalties:", error);
    }
  };

  const handleMonthChange = (e: SelectChangeEvent<string>) => {
    setSelectedMonth(e.target.value);
  };

  const handleYearChange = (e: SelectChangeEvent<string>) => {
    setSelectedYear(Number(e.target.value));
  };

  const handleDownloadExcel = async () => {
    try {
      const response = await fetchData<any>(
        `/documents/royalties/export?month=${selectedMonth}&year=${selectedYear}`,
        "GET",
        null,
        undefined,
        {
          responseType: "blob",
          headers: {
            Accept:
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          },
        }
      );

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = `royalties_${selectedYear}_${selectedMonth}.xlsx`;
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Errore durante il download:", error);
    }
  };

  // Verifica se un cliente ha effettuato più ordini nella stessa giornata
  const hasMultipleOrders = (royalty: Royalty) => {
    const customer = royalty.customerDisplayName;
    const destination = royalty.destinationDisplayName;
    // Consideriamo solo la data, ignorando l'orario
    const orderDate = moment(royalty.createdAt).format("YYYY-MM-DD");
    const key = `${customer}-${destination}-${orderDate}`;
    const orders = customerOrderDates.get(key);
    return orders && orders.length > 1;
  };

  return (
    <PageContainer>
      <Stack>
        <Typography variant="h6" mb={3} mt={1} align="center">
          GESTIONE ROYALTY
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={3}>
            <Select
              fullWidth
              value={selectedYear.toString()}
              onChange={handleYearChange}
              sx={{ backgroundColor: "white" }}
            >
              <MenuItem value={currentYear.toString()}>{currentYear}</MenuItem>
              <MenuItem value={(currentYear - 1).toString()}>
                {currentYear - 1}
              </MenuItem>
            </Select>
          </Grid>
          <Grid item xs={7}>
            <Select
              fullWidth
              value={selectedMonth}
              onChange={handleMonthChange}
              displayEmpty
              sx={{ minWidth: 200, backgroundColor: "white" }}
            >
              <MenuItem value="">
                <em>Seleziona un mese</em>
              </MenuItem>
              {months.map((month) => (
                <MenuItem key={month.value} value={month.value}>
                  {month.label}
                </MenuItem>
              ))}
            </Select>
          </Grid>
          <Grid item xs={2} sx={{ display: "flex", justifyContent: "center" }}>
            <IconButton
              color="primary"
              onClick={handleDownloadExcel}
              disabled={!selectedMonth}
            >
              <DownloadIcon />
            </IconButton>
          </Grid>
        </Grid>

        {state.isLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "80vh",
            }}
          >
            <CircularProgress />
          </Box>
        ) : royalties.length > 0 ? (
          <RoyaltyTable
            royalties={royalties}
            hasMultipleOrders={hasMultipleOrders}
          />
        ) : selectedMonth ? (
          <NoResult />
        ) : (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "40vh",
            }}
          >
            <Typography variant="body1" color="text.secondary">
              Seleziona un mese per visualizzare i dati
            </Typography>
          </Box>
        )}

        {/* Rimossa la paginazione */}
      </Stack>
    </PageContainer>
  );
};

export default RoyaltyComponent;
